// ABOUTME: Searchable dropdown component for selecting athletes
// ABOUTME: Used by coaches to filter data by specific athlete

import type { FC, RefObject } from "react"
import { useTranslation } from "react-i18next"
import { useZIndex } from "@shared/hooks/useZIndex"

interface Athlete {
  id?: string | null
  firstName?: string | null
  lastName?: string | null
}

interface AthleteSearchDropdownProps {
  selectedAthleteId: string
  selectedAthleteName: string
  filteredAthletes: Athlete[]
  athleteSearch: string
  athleteDropdownOpen: boolean
  athleteDropdownRef: RefObject<HTMLDivElement | null>
  setAthleteSearch: (value: string) => void
  setAthleteDropdownOpen: (value: boolean) => void
  onAthleteChange: (athleteId: string) => void
  className?: string
}

export const AthleteSearchDropdown: FC<AthleteSearchDropdownProps> = ({
  selectedAthleteId,
  selectedAthleteName,
  filteredAthletes,
  athleteSearch,
  athleteDropdownOpen,
  athleteDropdownRef,
  setAthleteSearch,
  setAthleteDropdownOpen,
  onAthleteChange,
  className = "w-[200px]",
}) => {
  const { t } = useTranslation()
  const zIndex = useZIndex(athleteDropdownOpen)

  return (
    <div
      ref={athleteDropdownRef}
      className={`relative ${className}`}
    >
      <input
        value={athleteDropdownOpen ? athleteSearch : selectedAthleteName}
        onChange={(e) => {
          setAthleteSearch(e.target.value)
          setAthleteDropdownOpen(true)
        }}
        onFocus={() => {
          setAthleteDropdownOpen(true)
          setAthleteSearch("")
        }}
        placeholder={t("statistics.searchAthlete")}
        className="w-full rounded-lg border border-gray-300 bg-white px-2.5 text-sm h-[30px]"
      />
      {athleteDropdownOpen && zIndex > 0 && (
        <div
          className="absolute mt-1 w-full max-h-60 overflow-auto border border-gray-300 rounded-lg bg-white shadow-lg"
          style={{ zIndex }}
        >
          {filteredAthletes.length > 0 ? (
            filteredAthletes
              .filter((athlete): athlete is Athlete & { id: string } => !!athlete.id)
              .map((athlete) => (
                <button
                  type="button"
                  key={athlete.id}
                  onClick={() => {
                    onAthleteChange(athlete.id)
                    setAthleteDropdownOpen(false)
                    setAthleteSearch("")
                  }}
                  className={`w-full text-left px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${
                    athlete.id === selectedAthleteId ? "bg-blue-50 text-blue-700" : ""
                  }`}
                >
                  {`${athlete.lastName || ""} ${athlete.firstName || ""}`.trim() || athlete.id}
                </button>
              ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">{t("statistics.noAthletesFound")}</div>
          )}
        </div>
      )}
    </div>
  )
}
